import React, { FC, useEffect, useState } from 'react'
import { graphql } from 'gatsby'

import { ClassesPageQueryQuery } from '@graphqlTypes'

import { isClientSide } from "@utils"

// HS Marketing Shared Components
import { DefaultPrefooter } from "@helpscout/hs-marketing-shared-components"

import SEO from '@components/SEO/SEO'

import ClassesPageClassDetails from './_components/ClassesPageClassDetails/ClassesPageClassDetails'
import ClassesPageHero from './_components/ClassesPageHero/ClassesPageHero'
import ClassesPageRecordedClassCard from './_components/ClassesPageRecordedClassCard/ClassesPageRecordedClassCard'

import {
  ClassesPageClassesDIV,
  ClassesPageRecordedClassesDIV,
  ClassesPageRecordedContentDIV
} from './index.styles'
import useCommonUrlsAndNumbers from '@hooks/useCommonUrlsAndNumbers'

export const Head = () => (
  <SEO
    description="Want to see Help Scout in action? Get a full tour of the app from one of our resident customer champions and get all of your questions answered."
    image="/images/pages/classes/ClassesPage--meta.jpg"
    permalink="/classes/"
    title="Live Product Walkthrough | Help Scout"
  />
)

const ClassesPage: FC<{ data: ClassesPageQueryQuery }> = ({
  data: {
    avatarsData: { nodes: avatars },
    classesData: { edges: classes },
    recordedClassesData: { edges: recordedClasses },
  }
}) => {
  const [filteredClasses, setFilteredClasses] = useState(classes)
  const { demoUrl, signupPlusUrl } = useCommonUrlsAndNumbers()

  useEffect(() => {
    // Check query params for client type and show specified classes depending on client type
    if (isClientSide()) {
      const params = new URLSearchParams(window.location.search)
      const headingParam = params.get("heading")

      if (headingParam) {
        switch (headingParam.toUpperCase()) {
          case "TRIAL":
            setFilteredClasses(classes.filter(({ node }) => node.title !== "User Onboarding Workshop"))
            break

          case "CUSTOMER":
            setFilteredClasses(classes.filter(({ node }) => node.title !== "Live Product Walkthrough"))
            break
        }
      }
    }
  }, [])

  return (
    <>
      <ClassesPageHero avatars={avatars} />
      <div className="container">
        <ClassesPageClassesDIV
          className={`classes-count--${filteredClasses.length}`}
        >
          {filteredClasses.map(({ node }) => (
            <ClassesPageClassDetails
              key={node.title}
              color={node.color}
              times={node.times}
              title={node.title}
              description={node.description}
              webinarKey={node.times && node.times.length > 0 && node.times[0]?.webinarKey}
            />
          ))}
        </ClassesPageClassesDIV>
        <div className="grid">
          <div className="column column--2-12-M column--3-11-L column--4-10-XL">
            <ClassesPageRecordedContentDIV>
              <h2>Don’t have time to catch us&nbsp;live?</h2>
              <p className="type--text-L">Check out our jumpstart videos, in-depth Help&nbsp;Scout walkthroughs, and best practice deep-dives to level up your support game!</p>
            </ClassesPageRecordedContentDIV>
          </div>
          <div className="column">
            <ClassesPageRecordedClassesDIV>
              {recordedClasses.map(({ node }) => (
                <ClassesPageRecordedClassCard
                  description={node.description}
                  key={node.title}
                  tag={node.tag}
                  title={node.title}
                  wistiaId={node.wistiaId}
                  wistiaPreviewImage={node.wistiaPreviewImage}
                  wistiaVideoLength={node.wistiaVideoLength}
                />
              ))}
            </ClassesPageRecordedClassesDIV>
          </div>
        </div>
      </div>
      <DefaultPrefooter {...{ demoUrl, signupPlusUrl }} />
    </>
  )
}

export const pageQuery = graphql`
  query ClassesPageQuery {
    avatarsData: allContentfulPerson(
      filter: {
        name: {in: [
        "Alison Groves",
        "Kristi Thompson",
        "Christine Chavez",
        "Liam Doherty",
        ]}
      }
    ) {
      nodes {
        name
        image {
          imgixImage {
            gatsbyImageData(width: 50)
          }
        }
      }
    }
    classesData: allContentfulClass(
      sort: {order: ASC}
      filter: {
        isRecorded: {eq: false}
        # Test class
        contentful_id: {ne: "521AJf4hxIduxJJHR6Yzfc"}
      }
    ) {
      edges {
        node {
          color
          description
          order
          title
          times {
            timestamp
            webinarKey
          }
        }
      }
    }
    recordedClassesData: allContentfulClass(
      sort: {order: ASC}
      filter: {
        isRecorded: {eq: true}
        # Test class
        contentful_id: {ne: "521AJf4hxIduxJJHR6Yzfc"}
      }
    ) {
      edges {
        node {
          description
          order
          tag
          title
          wistiaId
          wistiaPreviewImage {
            imgixImage {
              url
              gatsbyImageData(width: 625)
            }
          }
          wistiaVideoLength
        }
      }
    }
  }
`

export default ClassesPage
